import { UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import { fpDivide, fpMultiply, toNumber } from '@/helpers/unit-formatter/unit-formatter.utils';

import { SUPPORTED_UNITS } from '../unit-formatter.constants';

/**
 * Transforms temperature values from Fahrenheit to other temperature scales.
 * Supports conversion to Celsius (°C) and Kelvin (°K).
 * Uses floating-point safe arithmetic operations for accurate conversions.
 *
 * @param {UnitValue} unitValue - The temperature value in Fahrenheit
 * @param {any} unitValue.value - The numeric temperature value in Fahrenheit
 * @param {string} [unitValue.units] - The current units (should be °F or similar)
 * @param {string} [newUnits] - Target temperature scale ('°C', '°K')
 * @returns {UnitValue} Transformed temperature value in the target units
 *   - Returns original value if newUnits is not recognized
 *   - Returns original value if input value is null/undefined
 *
 * @example
 * transformFahrenheit({ value: 32, units: '°F' }, '°C')
 * // Returns: { value: 0, units: '°C' }
 *
 * @example
 * transformFahrenheit({ value: 212, units: '°F' }, '°C')
 * // Returns: { value: 100, units: '°C' }
 *
 * @example
 * transformFahrenheit({ value: 32, units: '°F' }, '°K')
 * // Returns: { value: 273.15, units: '°K' }
 */
export const transformFahrenheit = (unitValue: UnitValue, newUnits?: string): UnitValue => {
  const value = toNumber(unitValue.value);
  if (value == null || value == undefined) return unitValue;

  switch (newUnits?.toLowerCase()) {
    case SUPPORTED_UNITS.DEGREES_C:
      return {
        value: fpDivide(fpMultiply(value - 32, 5), 9),
        units: newUnits,
      };
    case SUPPORTED_UNITS.DEGREES_K:
      return {
        value: fpDivide(fpMultiply(value + 459.67, 5), 9),
        units: newUnits,
      };
    default:
      return unitValue;
  }
};
